import { useEffect } from "react";
import { useNavigate, useRouteError } from "react-router";
import { recordErrorToRum } from "utils/cloudWatchRum";
import { Box, Typography, Button, Paper, Stack, useTheme } from "@mui/material";
import { House, TriangleAlert } from "lucide-react";
import { Link, palette } from "@powerdiary/components-library/lib";
import { ROUTES } from "../../../routes";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import Footer from "../../Layout/Footer";
import Header from "../../Layout/Header";

export const ErrorPage = () => {
    const theme = useTheme();
    const { clientPortalGuid } = useClientPortalConfigContext();
    const navigate = useNavigate();
    const routeError = useRouteError();

    useEffect(() => {
        document.title = "Error - Client Bookings Zanda";
    }, []);

    useEffect(() => {
        if (routeError instanceof Error) {
            recordErrorToRum(routeError);
        }
    }, [routeError]);

    const navigateHome = () => {
        void navigate(`/clientportal/${clientPortalGuid}/${ROUTES.HOME.urlPath}`);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
            <Header />

            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
                sx={{ flex: 1 }}
                data-test-id="section_error_page"
            >
                <Paper sx={{ width: "600px" }}>
                    <Stack spacing={3} p={4} alignItems="center">
                        <TriangleAlert
                            size={28}
                            strokeWidth={2}
                            color={theme.palette.primary.main}
                            data-test-id="icon_error"
                            aria-hidden
                        />
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="heading3" textAlign="center" data-test-id="text_error_title">
                                Uh-oh! This page doesn&apos;t seem to be working
                            </Typography>
                            <Typography
                                variant="paragraphMediumRegular"
                                textAlign="center"
                                data-test-id="text_error_subtitle"
                            >
                                We&apos;re very sorry about this.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                background: palette.neutral[50],
                                borderRadius: 1,
                                padding: 2,
                            }}
                            data-test-id="section_error_instructions"
                        >
                            <Typography variant="paragraphMediumBold" data-test-id="text_instructions_title">
                                What can you do?
                            </Typography>
                            <ul style={{ margin: 0 }}>
                                <li>
                                    <Typography variant="paragraphMediumRegular" data-test-id="text_instruction_reload">
                                        Please try{" "}
                                        <Link
                                            variant="paragraphMediumBold"
                                            onClick={() => window.location.reload()}
                                            data-test-id="link_reload"
                                        >
                                            reloading
                                        </Link>{" "}
                                        the page or return to the{" "}
                                        <Link
                                            variant="paragraphMediumBold"
                                            onClick={navigateHome}
                                            data-test-id="link_home"
                                        >
                                            home
                                        </Link>{" "}
                                        page.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography
                                        variant="paragraphMediumRegular"
                                        data-test-id="text_instruction_contact"
                                    >
                                        If you&apos;re still having trouble, contact your practice directly for help.
                                    </Typography>
                                </li>
                            </ul>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<House size={14} strokeWidth={2} aria-hidden />}
                            onClick={navigateHome}
                            data-test-id="button_home"
                        >
                            Home
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <Footer />
        </Box>
    );
};
